<template>
  <div class="justify-between items-center h-full p-5"
  :class="classes">
    <router-link :to="{ name: 'product', params: { sku: product.sku } }" class="w-full h-full">
      <base-image
        :imageSource="product.cover"
        width="w-full h-full object-cover object-center"
      />
    </router-link>
    <base-footer
      :direction="footerDirection"
      width="w-full"
      class="flex-grow h-1/2 pt-2"
    >
      <template v-slot:main>
        <p>
          {{ headerContent }}
        </p>
        <router-link :to="{ name: 'product', params: { sku: product.sku } }" class="w-full">
          <p class="font-medium text-base hover:text-lh_red dark:hover:text-lh_gold">
            {{ bodyContent }}
          </p>
        </router-link>
      </template>
      <template v-slot:footer>
        <div v-for="detail in layout.footerDetails" :key="detail" class="first:flex hidden">
          <p class="text-base">
            {{ footerDetailContent(detail) }}
          </p>
        </div>
        <order-alert v-show="isAlreadyOrdered" :orderedQty="orderedQuantity" />
        <!-- <h4 class="text-base 2xl:text-xl my-1">
          {{ $n((product.price * discount) / 100, 'currency') }}
        </h4> -->
        <h4 class="text-base 2xl:text-xl my-1">
          {{ $n(product.price / 100, 'currency') }}
        </h4>
        <!--<div class="w-full flex space-x-2 items-center">
          <div class="w-4 h-4 rounded" :class="availabilityColors"></div>
          <p class="text">
            {{ currentStateOrder }}
            <span class="font-medium" v-if="showAvailableQuantity">
              {{ quantityAvailable }}
            </span>
          </p>
        </div>-->
      </template>
      <template v-slot:buttons>
        <div class="flex w-full space-x-4 items-center justify-between">
          <div class="w-1/2 flex place-self-end">
            <inc-dec
              :appOptions="{
                min: product.minOrderQtyLimit,
                max: product.maxOrderQtyLimit
              }"
              v-model="qty"
              component-width="w-16"
              :class="hideCart"
            />
          </div>
          <ShoppingBagIcon
            @click="addToCart()"
            class="cursor-pointer hover:text-lh_red dark:hover:text-lh_gold w-5 h-5"
            :class="hideCart"
          />
        </div>
      </template>
    </base-footer>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import { mapGetters, mapActions } from 'vuex';
import { addProductToCart } from '@/utils/product-service';
import IncDec from '@/components/base/IncDec.vue';
import 'vue3-carousel/dist/carousel.css';
import BaseFooter from '@/components/base/BaseFooter.vue';
import BaseImage from '@/components/base/BaseImage.vue';
import dayjs from 'dayjs';
import OrderAlert from '@/components/base/OrderAlert.vue';
import { ShoppingBagIcon } from '@heroicons/vue/24/outline';

export default {
  setup() {
    const toast = useToast();

    return { toast };
  },
  components: {
    ShoppingBagIcon,
    BaseFooter,
    IncDec,
    BaseImage,
    OrderAlert,
  },
  data() {
    return {
      currentVariant: 0,
      directionClass: {
        landscape: 'flex flex-row',
        portrait: 'flex flex-col',
      },
      dimensionClass: {
        full: 'w-full',
        quarter: 'w-full sm:w-1/3 lg:w-1/4',
      },
      qty: this.product.minOrderQtyLimit,
      settings: {
        itemsToShow: 1,
        snapAlign: 'center',
      },
      isAlreadyOrdered: false,
      orderedQuantity: 0,
      // discountPercent: 0,
    };
  },
  props: {
    direction: {
      type: String,
      default: 'portrait',
    },
    width: {
      type: String,
      default: 'full',
    },
    elementsColor: {
      type: String,
      default: 'text-black dark:text-white',
    },
    productDescription: {
      type: String,
    },
    product: {
      type: Object,
    },
    hasVariants: {
      type: Boolean,
    },
    footerDirection: {
      type: String,
      default: 'vertical',
    },
  },
  watch: {
    product: {
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.qty = this.product.minOrderQtyLimit;
          this.isAlreadyOrdered = false;
          this.orderedQuantity = 0;
          this.checkOnOrderQuantity();
        }
      },
    },
  },
  async created() {
    this.checkOnOrderQuantity();
    // this.discountPercent = 10;
  },
  computed: {
    ...mapGetters({
      layouts: 'layouts/layouts',
    }),
    layout() {
      if (this.layouts[this.product.productCategorySlug]) {
        return this.layouts[this.product.productCategorySlug].CardProductVertical;
      }
      return this.layouts.default.CardProductVertical;
    },
    classes() {
      return `${this.directionClass[this.direction]} ${this.dimensionClass[this.width]} ${this.elementsColor}`;
    },
    headerContent() {
      if (this.layout.header.type === 'attribute') {
        return this.product.attributes[this.layout.header.value]
          ? this.product.attributes[this.layout.header.value].value
          : '';
      }

      return this.product[this.layout.header.value];
    },
    bodyContent() {
      if (this.layout.body.type === 'attribute') {
        return this.product.attributes[this.layout.body.value]
          ? this.product.attributes[this.layout.body.value].value
          : '';
      }

      if (this.product.translations?.[this.layout.body.value]?.[this.$i18n.locale]) {
        return this.product.translations[this.layout.body.value][this.$i18n.locale];
      }

      return this.product[this.layout.body.value];
    },
    inTwoDays() {
      return dayjs().add(2, 'day').format('YYYY-MM-DD');
    },
    availabilityColors() {
      if (this.product.availableFrom >= this.inTwoDays) {
        return 'bg-blue-400';
      }
      if (this.product.availableForOrders && this.outOfStockOrder) {
        return 'bg-orange-400';
      }
      if (this.product.availableForOrders && this.discontinuedStock) {
        return 'bg-green-600';
      }
      if (this.product.isDiscontinued) {
        return 'bg-red-500';
      }
      return 'bg-transparent';
    },
    hideCart() {
      return this.product.isDiscontinued && this.product.outOfStock ? 'hidden' : 'flex';
    },
    today() {
      return dayjs(new Date()).format('YYYY-MM-DD');
    },
    discontinuedStock() {
      return !this.product.isDiscontinued && !this.product.outOfStock;
    },
    outOfStockOrder() {
      return this.product.outOfStock && !this.product.isDiscontinued;
    },
    availabilityOrder() {
      return this.product.availableForPreorders && this.availableFromTomorrow;
    },
    availabilityJustForPreorder() {
      return this.product.availableFrom > this.today;
    },
    showAvailableQuantity() {
      return this.product.availableForOrders
      && !this.product.outOfStock
      && this.product.availableFrom <= this.inTwoDays;
    },
    currentStateOrder() {
      if (this.outOfStockOrder) {
        return this.$t('frontoffice.products.outOfStock');
      }
      if (this.product.isDiscontinued) {
        return `${this.$t('frontoffice.products.isDiscontinued')}${this.showAvailableQuantity ? ':' : ''}`;
      }
      if (this.availabilityJustForPreorder) {
        return `${this.$t('frontoffice.products.upcoming')}`;
      }
      return `${this.$t('frontoffice.products.available')}:`;
    },
    quantityAvailable() {
      if (this.product.available > 50) {
        return 50;
      }
      if (this.product.available >= 0) {
        return this.product.available;
      }
      return 0;
    },
    // discount() {
    //   return 1 - this.discountPercent / 100;
    // },
  },
  methods: {
    ...mapActions({
      alreadyOrdered: 'customers/alreadyOrdered',
    }),
    attributeValues(product) {
      return product.attributes.map((av) => (av.attributeValue)).join('-');
    },
    addToCart() {
      addProductToCart(this.product, this.qty);
      this.toast.info(this.$t('frontoffice.cart.messages.productAddedToCart'));
    },
    footerDetailContent(detail) {
      return detail.type === 'attribute' && this.product.attributes.length > 0
        ? this.product.attributes[detail.value].value
        : this.product[detail.value];
    },
    async checkOnOrderQuantity() {
      const isOrdered = await this.alreadyOrdered(this.product.sku);

      if (isOrdered.found) {
        this.isAlreadyOrdered = true;
        this.orderedQuantity = isOrdered.quantity;
      }
    },
  },
};
</script>

<style scoped>
.text {
  @apply font-thin text-base text-left
}
</style>
